<script setup lang="ts">
import { Home } from 'lucide-vue-next';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { RouterLink, useRoute } from 'vue-router';

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';

const route = useRoute();
const { t } = useI18n();

const ROUTE_LABELS: Record<string, string> = {
  '/': 'sidebar.dashboard',
  '/projects': 'sidebar.projects',
  '/contractors': 'contractor.title',
  '/contractors/create': 'contractor.create',
  '/settings': 'sidebar.settings',
};

interface BreadcrumbEntry {
  readonly path: string;
  readonly label: string;
}

const breadcrumbs = computed<BreadcrumbEntry[]>(() => {
  const segments = route.path.split('/').filter(Boolean);
  const items: BreadcrumbEntry[] = [];

  let accumulated = '';

  for (const segment of segments) {
    accumulated += `/${segment}`;
    const labelKey = ROUTE_LABELS[accumulated];

    if (labelKey) {
      items.push({ path: accumulated, label: t(labelKey) });
    }
  }

  return items;
});
</script>

<template>
  <Breadcrumb>
    <BreadcrumbList>
      <BreadcrumbItem>
        <BreadcrumbLink as-child>
          <RouterLink to="/">
            <Home class="h-4 w-4" />
          </RouterLink>
        </BreadcrumbLink>
      </BreadcrumbItem>

      <template v-for="(crumb, index) in breadcrumbs" :key="crumb.path">
        <BreadcrumbSeparator />

        <BreadcrumbItem>
          <BreadcrumbPage v-if="index === breadcrumbs.length - 1">
            {{ crumb.label }}
          </BreadcrumbPage>
          <BreadcrumbLink v-else as-child>
            <RouterLink :to="crumb.path">
              {{ crumb.label }}
            </RouterLink>
          </BreadcrumbLink>
        </BreadcrumbItem>
      </template>
    </BreadcrumbList>
  </Breadcrumb>
</template>
